import { useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Banner, Flex, FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { useQueryParam } from "~/common/hooks/useQueryParams";
import { AgentJobStatus } from "~/graphql/supergraphEnums";
import {
  useGetCountriesQuery,
  useGetDocumentExtractedDataQuery,
} from "~/graphql/supergraphHooks";
import { DocumentsViewPanel } from "../../CaseDetailsDrawer/Documents/DocumentManagement/DocumentManagementDisplay/DocumentsViewPanel";
import { checkIsUsCountrySelected } from "../../CaseDetailsDrawer/Profiles/common/AddressFields/StateFormField/utils";
import { type RoleState } from "../../CaseDetailsDrawer/Profiles/common/AutoProfileCreationCard";
import CreateEntityForm from "../../CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm";
import CreateIndividualForm from "../../CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm";
import { ExitWarningModal } from "../../Common/ExitWarningModal";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { findCountryMatch } from "../CaseCreationPreview/countryMatcher";
import { findUsStateMatch } from "../CaseCreationPreview/stateMatcher";
import { previewBody, previewPanel, rightPanel, rightPanelWrapper } from "./styles_vanilla.css";

type ProfileCreationPreviewProps = {
  parentProfileId?: number;
  isBeneficiaryProfile?: boolean;
  isJointChild?: boolean;
  onCancel: () => void;
  onComplete: (profileId: number) => void;
};

export const ProfileCreationPreview = ({
  parentProfileId,
  isBeneficiaryProfile = false,
  isJointChild = false,
  onCancel,
  onComplete,
}: ProfileCreationPreviewProps) => {
  const { caseId } = useCurrentCaseContext();
  const location = useLocation<RoleState | undefined>();
  const { profileType } = useParams<{ profileType?: "individual" | "entity" }>();
  const [docId] = useQueryParam("documentId");
  const [posId] = useQueryParam("positionId");
  const [categoryParam] = useQueryParam("category");
  const resolvedProfileType = profileType ?? categoryParam;
  const roleState: RoleState | undefined = isJointChild
    ? { isOwner: true, isController: false, ownershipPercentage: 0 }
    : location.state;
  const documentId = docId ? parseInt(docId) : 0;
  const positionId = posId ?? "0";

  const documentExtractedData = useGetDocumentExtractedDataQuery({
    variables: {
      docId: documentId,
    },
    skip: !documentId,
  });
  const extractedData = documentExtractedData.data?.document?.agentExtractedData as Record<
    string,
    string
  >;
  const extractionJobStatus = documentExtractedData.data?.document?.agentJobStatus;

  const countriesResult = useGetCountriesQuery({
    skip: !documentId,
  });

  const matchedCountry = useMemo(
    () =>
      extractedData && countriesResult.data
        ? findCountryMatch(extractedData["country"], countriesResult.data.complianceCountries)
        : undefined,
    [extractedData, countriesResult.data],
  );

  const extractedDataWithNormalizedValues = useMemo(() => {
    const countryCode = matchedCountry?.alpha2code ?? extractedData?.["country"];
    const rawState = extractedData?.["state"];
    const normalizedState = checkIsUsCountrySelected(countryCode)
      ? (findUsStateMatch(rawState) ?? "")
      : rawState;

    return {
      ...extractedData,
      country: countryCode,
      state: normalizedState,
    };
  }, [extractedData, matchedCountry]);

  const shouldShowAutoFilledBanner = extractionJobStatus !== AgentJobStatus.Failed;

  const [showExitWarning, setShowExitWarning] = useState(false);

  return (
    <QueryLoader result={[documentExtractedData, countriesResult]}>
      <FullscreenTakeover.Body className={previewBody}>
        <Flex className={previewPanel}>
          <DocumentsViewPanel documentId={documentId} />
        </Flex>
        <Flex className={rightPanelWrapper}>
          <Flex className={rightPanel}>
            {shouldShowAutoFilledBanner && (
              <Banner
                variant="success"
                icon="circle-bolt"
                title="We've filled in the data from your document. Please review and fill in any missing information."
              />
            )}
            {resolvedProfileType === "individual" ? (
              <CreateIndividualForm
                caseId={caseId}
                parentProfileId={parentProfileId}
                positionId={positionId}
                saveButtonLabel={isJointChild ? "Save and exit" : "Next"}
                navigateOnCompletion={onComplete}
                navigateOnCancel={() => setShowExitWarning(true)}
                isBeneficiaryProfile={isBeneficiaryProfile}
                isChildOfJointAccount={isJointChild}
                extractedData={extractedDataWithNormalizedValues}
                initialRoleValues={roleState}
              />
            ) : (
              <CreateEntityForm
                caseId={caseId}
                parentProfileId={parentProfileId}
                positionId={positionId}
                saveButtonLabel="Next"
                navigateOnCompletion={onComplete}
                navigateOnCancel={() => setShowExitWarning(true)}
                isBeneficiaryProfile={isBeneficiaryProfile}
                extractedData={extractedDataWithNormalizedValues}
                initialRoleValues={roleState}
              />
            )}
          </Flex>
        </Flex>
      </FullscreenTakeover.Body>
      <ExitWarningModal
        show={showExitWarning}
        onCancel={() => setShowExitWarning(false)}
        onConfirm={() => {
          setShowExitWarning(false);
          onCancel();
        }}
      />
    </QueryLoader>
  );
};
